// Imports ------------------------------------------------------------------->

import {Service, service, inject} from "./di";
import {PostcardService, ClippedCouponsJSON, PostcardMode} from "./PostcardService";
import {LoyaltyProgramService} from "./LoyaltyProgramService";
import {LocationService} from "./LocationService";
import {ConfigService, RuntimeConfig} from "./ConfigService";
import {FlyerService} from "./FlyerService";
import {StorageService} from "./StorageService";
import {AuthenticationService} from "./AuthenticationService";
import {Coupon, FlyerItemCoupon, CouponCategory, CouponRedemptionStatus,
				ActiveClippedCoupons, ClippedCoupon} from "../definitions/CouponDefinitions";
import {Flyer} from "../definitions/FlyerDefinitions";
import {DataJSON, TermsJSON} from "../definitions/BackflippDefinitions";
import {LoyaltyProgramCoupon, Category, LoyaltyCard} from "../definitions/CommonDefinitions";
import {AuthUser} from "./definitions/IAuthenticationService";
import * as array from "../utils/array";
import {Backflipp} from "./BackflippService";
import { isHeroCoupon, compareCoupon } from "../utils/coupons";
import { DateTimeService } from "./DateTimeService";


// Private Definitions ------------------------------------------------------->

function liftCoupon(json: DataJSON, coupon_raw: any, matchups_raw: any[] = []): Coupon {
	const coupon = new Coupon(coupon_raw);
	coupon.matchUps = matchups_raw.map((matchup) =>
			liftFlyerItemCoupon(json, matchup));
	return coupon;
}

function liftFlyerItemCoupon(json: DataJSON, raw: any): FlyerItemCoupon {
	return {
		id: raw.id,
		flyerId: raw.flyer_id,
		flyer: new Flyer(array.find(json.flyers,
				(flyer) => flyer.id === raw.flyer_id)),
		flyerItemId: raw.flyer_item_id,
		merchant: raw.merchant,
		merchantLogo: raw.merchant_logo,
		couponId: raw.coupon_id,
		left: raw.left,
		bottom: raw.bottom,
		right: raw.right,
		top: raw.top,
		item: {
			id: raw.flyer_item_id,
			currentPrice: raw.item_current_price,
			originalPrice: raw.item_original_price,
			dollarsOff: raw.item_dollars_off,
			cutoutUrl: raw.item_cutout_url
		},
		couponDollarsOff: raw.coupon_dollars_off,
		totalSavings: raw.total_savings,
		finalPrice: raw.final_price,
		percentOff: raw.percent_off,
		displayFinalPrice: raw.display_final_price,
		displayType: raw.display_type,
		itemRank: raw.item_rank
	};
}

function liftLoyaltyProgramCoupon(raw: any): LoyaltyProgramCoupon {
	const dtService = DateTimeService.getInstance();
	return {
		id: raw.id,
		loyaltyProgramId: raw.loyalty_program_id,
		couponId: raw.coupon_id,
		externalId: raw.external_id,
		validFrom: raw.valid_from ? dtService.convertTimeToZulu(raw.valid_from) : null,
		validTo: raw.valid_to ? dtService.convertTimeToZulu(raw.valid_to, true) : null,
		availableFrom: raw.available_from ? dtService.convertTimeToZulu(raw.available_from) : null,
		availableTo: raw.available_to ? dtService.convertTimeToZulu(raw.available_to, true) : null,
		redemptionMethod: raw.redemption_method
	};
}

type SoftClippingRecord = { clippedId: number,
	expiry: number,
	// V2 of soft clippings has user id so they are on a per-user basis
	userId?: string
};

// Public Definitions -------------------------------------------------------->

@service
export class CouponService extends Service {

	@inject flyerService: FlyerService;
	@inject loyaltyService: LoyaltyProgramService;
	@inject postcardService: PostcardService;
	@inject locationService: LocationService;
	@inject config: ConfigService;
	@inject backflipp: Backflipp;
	@inject storage: StorageService;
	@inject auth: AuthenticationService;

	private _disclaimerCache: { [couponId: string]: Promise<string>  } = {};
	private _clippedCouponsJSON: Promise<ClippedCouponsJSON>;
	private _clippedCoupons: SoftClippingRecord[] = this._loadClippedCoupons();

	constructor() {
		super();
		/* Clipped coupons are stored per-user. Listen for auth event so we can
			* clear cache when user logs out*/
		window.addEventListener("flipp-auth", this._handleAuthEvent);
	}

	clearClippedCoupons() {
		this._clippedCouponsJSON = null;
	}

	getCoupon(couponId: number) {
		return this.flyerService.getFlyersDataJSON(this.locationService.postalCode).then((dataJSON) => {
			const target = array.find(dataJSON.coupons, (coupon) =>
					coupon.coupon_id === couponId);

			if (target && (target.redemption_method === "print" || target.redemption_method === "savetocard")) {
				const flyersById: any = {};
				dataJSON.flyers.forEach((flyer) => flyersById[flyer.id] = flyer);

				const matchUps = dataJSON.flyer_item_coupons
						.filter((item) => item.coupon_id === couponId)

						// NOTE: This also excludes matchups with invalid flyer ids
						//       this has happened, and should not be removed.
						.filter((matchup) => {
							const flyer = flyersById[matchup.flyer_id];
							return flyer;
						})

						// FIXME data munge
						.map((item) => {
							const flyer = flyersById[item.flyer_id];
							if (flyer) {
								item.merchant = flyer.merchant;
								item.merchant_logo = flyer.merchant_logo;
							}
							return item;
						});

				matchUps.sort((a, b) => {
					return (a.item_rank !== b.item_rank) ?
							a.item_rank - b.item_rank : // item_rank ASC
							b.id - a.id;                // id DESC
				});

				return liftCoupon(dataJSON, target, matchUps);
			}

			return null;
		});
	}

	getCouponsForFlyer(flyerId: number) {
		return this.flyerService.getFlyersDataJSON(this.locationService.postalCode).then((dataJSON) => {
			return this.flyerService.getFlyerDetails(flyerId).then((details) => {
				const itemIdSet: any = {};
				details.items.forEach((item) => {
					itemIdSet[item.id.toString()] = true;
				});

				return dataJSON.flyer_item_coupons
						.filter((item) => {
							const coupon = array.find(dataJSON.coupons, (c) =>
									c.coupon_id === item.coupon_id);

							return item.flyer_item_id.toString() in itemIdSet &&
									coupon && (coupon.redemption_method === "print" ||
										coupon.redemption_method === "savetocard");
						})
						.map((fic) => liftFlyerItemCoupon(dataJSON, fic));
			});
		});
	}

	getCoupons(redemptionMethod?: string, postalCode: string = this.locationService.postalCode) {
		return this.flyerService.getFlyersDataJSON(postalCode).then((dataJSON) => {
			// lift flyers into map by flyer id, so we can get the flyer merchant
			// name per matchup
			// FIXME another backflipp thing.
			const flyersById: any = {};
			dataJSON.flyers.forEach((flyer) => flyersById[flyer.id] = flyer);

			return dataJSON.coupons
				.filter((coupon) => redemptionMethod ?
					coupon.redemption_method === redemptionMethod : true)
				.map((coupon) => {
					const matchUps = dataJSON.flyer_item_coupons
							.filter((item) => item.coupon_id === coupon.coupon_id)

							// NOTE: This also excludes matchups with invalid flyer ids
							//       this has happened, and should not be removed.
							.filter((matchup) => {
								const flyer = flyersById[matchup.flyer_id];
								return flyer;
							})

							// FIXME data munge
							.map((item) => {
								const flyer = flyersById[item.flyer_id];
								if (flyer) {
									item.merchant = flyer.merchant;
									item.merchant_logo = flyer.merchant_logo;
								}
								return item;
							});

					matchUps.sort((a, b) => {
						return (a.item_rank !== b.item_rank) ?
								a.item_rank - b.item_rank : // item_rank ASC
								b.id - a.id;                // id DESC
					});

					return liftCoupon(dataJSON, coupon, matchUps);
				});
		});
	}

	async getSortedCoupons(redemptionMethod?: string, postalCode: string = this.locationService.postalCode) {
		const heroCoupons: Coupon[] = [];
		const otherCoupons: Coupon[] = [];
		const coupons = await this.getCoupons(redemptionMethod, postalCode);
		coupons.forEach((coupon: Coupon) => {
			if (isHeroCoupon(coupon)) {
				heroCoupons.push(coupon);
			} else {
				otherCoupons.push(coupon);
			}
		});

		heroCoupons.sort(compareCoupon);
		otherCoupons.sort(compareCoupon);

		return [...heroCoupons, ...otherCoupons];
	}

	getCouponsForFlyerItem(itemId: number) {
		return this.flyerService.getFlyersDataJSON(this.locationService.postalCode).then((dataJSON) => {
			// Get the 'flyer_item_coupons', we need to map these to coupon objects.
			const idSet: any = {};
			dataJSON.flyer_item_coupons.filter((item) => {
				return item.flyer_item_id === itemId;
			}).forEach((flyer_item_coupon) => {
				idSet[flyer_item_coupon.coupon_id] = true;
			});

			return dataJSON.coupons.filter((coupon) => {
				return (coupon.coupon_id in idSet) && (coupon.redemption_method === "print" ||
					coupon.redemption_method === "savetocard");
			}).map((coupon) => liftCoupon(dataJSON, coupon));
		});
	}

	getClippedCoupons(user: AuthUser) {
		return Promise.all([
			this._getClippedCoupons(user),
			this.getLoyaltyProgramCoupons()
		]).then(([clippedCoupons, programCoupons]) => {
			const coupons = clippedCoupons.loyalty_program_coupons;
			return coupons.map((coupon) => {
				const program = array.find(programCoupons,
					(prog) => prog.id === coupon.id);
				if (!program) {
					return null;
				}
				return {
					program,
					status: coupon.status === "clipped" ?
										CouponRedemptionStatus.CLIPPED :
									coupon.status === "redeemed" ?
										CouponRedemptionStatus.REDEEMED :
									coupon.status === "unavailable" ?
										CouponRedemptionStatus.UNAVAILABLE : null
				} as ClippedCoupon;
			}).filter((coupon) => coupon !== null);
		});
	}

	getCouponDisclaimer(id: string) {
		if (!this._disclaimerCache[id]) {
			this._disclaimerCache[id] =
				this.backflipp.getCouponDetails([id]).then((details: TermsJSON) => {
					const entry = array.find(details.coupons, (item) => item.id === Number(id));
					if (!entry) {
						return null;
					}
					return entry.disclaimer_text;
				}).catch((e: Error) => {
					return null;
				});
		}
		return this._disclaimerCache[id];
	}

	/* Helper function which returns only the ACTIVE clipped coupons. An active clipped coupon
		* is a coupon with an associated loyalty card. This is helpful because there are certain cases
		* where POSTcard returns a coupon as 'clipped' but the user no longer has an associated loyalty
		* card (it was deleted) or they have changed their FSA (eg: clipped coupons in US FSA and then
		* switched to CA) */
	getActiveClippedCoupons(user: AuthUser) {
		return Promise.all([
			this.loyaltyService.getLoyaltyCards(user),
			this.getClippedCoupons(user)
		]).then(([cards, clipped]) => {
			const availableCards = cards.map((card) => card.loyaltyProgramId);
			const clippedCoupons: ActiveClippedCoupons = {};
			clipped.forEach((clippedCoupon) => {
				if (availableCards.indexOf(clippedCoupon.program.loyaltyProgramId) !== -1) {
					clippedCoupons[clippedCoupon.program.couponId] = clippedCoupon;
				}
			});

			return clippedCoupons;
		});
	}

	clipCoupon(userId: string, card: LoyaltyCard, coupon: LoyaltyProgramCoupon, mode = PostcardMode.Default) {
		return this.postcardService.clipCoupon(userId, card, coupon, mode).then(() => {
			/* Postcard API will return a 200 when a coupon is clipped successfully, but
				* there may be a delay as the clipping flows through Cellfire/Inmar/etc.
				* Consequently, calling getClippedCoupons() may not actually
				* return the coupon we just clipped. To get around this, we do a 'soft'
				* clip and store all clipped coupons in localStorage. When
				* getClippedCoupons() is called, we 'merge' the 'soft' clippings in
				* localStore with the list returned from postcard. This way, our UI can
				* still report the coupon as clipped while the delay is ongoing */
			this._addClippedCoupon(coupon);
			return;
		});
	}

	getLoyaltyProgramCoupons() {
		return this.flyerService.getFlyersDataJSON(this.locationService.postalCode).then((data) => {
			const coupons = data.loyalty_program_coupons;
			return coupons.map((coupon) => liftLoyaltyProgramCoupon(coupon));
		});
	}

	getCouponCategories(): Promise<Category[]> {
		const postalCode = this.locationService.postalCode;
		return this.flyerService.getFlyersDataJSON(postalCode).then((data) => {

			return data.coupon_category_sort_csv.split(",").
					map((category: string) => {
						const result = new CouponCategory(category);

						for (const coupon of data.coupons) {
							if ((coupon.redemption_method === "print" ||
									coupon.redemption_method === "savetocard") &&
									coupon.categories.indexOf(category) >= 0)
								++result.count;
						}

						return result;
					}).filter((category) => {
						return category.count > 0;
					});
		});
	}

	private _getClippedCoupons = (user: AuthUser) => {
		if (!this._clippedCouponsJSON) {
			this._clippedCouponsJSON = this.loyaltyService.getLoyaltyCards(user).then((cards) => {
				const userId = this.config.RUNTIME === RuntimeConfig.PG ?
					user.info.id : user.token.user_id;
				const postcardMode = this.config.RUNTIME === RuntimeConfig.PG ?
					PostcardMode.PG : PostcardMode.Default;
				return this.postcardService.getCardCoupons(userId, cards, postcardMode).then((coupons: ClippedCouponsJSON) => {

					/* Maintain a hash of clipped coupons (as returned by postcard) by ID */
					const clippedIds: { [key: number]: boolean } = {};
					coupons.loyalty_program_coupons.forEach((coupon) =>
						clippedIds[coupon.id] = true);

					/* Merge the 'soft' clipped coupons with the coupons returned by postcard.
						* If any of the 'fake' coupons have expired or are now being returned
						* by postcard then remove them from the saved list */
					const _validCoupons: SoftClippingRecord[] = [];
					const time = new Date().getTime();
					this._clippedCoupons.forEach((entry) => {
						const userId = this.config.RUNTIME === RuntimeConfig.PG ?
							this.auth.user.info.id : this.auth.user.token.user_id;
						if (entry.expiry > time && !clippedIds[entry.clippedId]) {

							/* For clippings w/o a userId (v1 clippings), assume they are all from the current user */
							if (!entry.userId)
								entry.userId = userId;

							/* Only if soft clipping is for the current user should we
							 * include it in the list of clipped coupons */
							if (entry.userId && entry.userId === userId) {
								coupons.loyalty_program_coupons.push({
									status: CouponRedemptionStatus.CLIPPED.toString(),
									id: entry.clippedId
								});
							}
							_validCoupons.push(entry);
						}
					});
					if (this._clippedCoupons.length !== _validCoupons.length) {
						this._clippedCoupons = _validCoupons;
						this._saveClippedCoupons();
					}

					return coupons;
				});
			}).catch((err: Error) => {
				return Promise.resolve<ClippedCouponsJSON>({
					loyalty_program_coupons: []
				});
			});
		}
		return this._clippedCouponsJSON;
	}

	private _addClippedCoupon = (coupon: LoyaltyProgramCoupon) => {
		const expiring = coupon.validTo ? coupon.validTo.getTime() : new Date().getTime() + 600000;
		const userId = this.config.RUNTIME === RuntimeConfig.PG ?
			this.auth.user.info.id : this.auth.user.token.user_id;
		const existing = array.find(this._clippedCoupons,
			(entry) => entry.clippedId === coupon.id && entry.userId === userId);

		if (existing) {
			return;
		}

		// Save soft clippings with user id so it doesn't "leak" to other users
		this._clippedCoupons.push({clippedId: coupon.id, expiry: expiring, userId});
		this._saveClippedCoupons();
	}

	private _saveClippedCoupons = () => {
		this.storage.setItem("clipped_coupons", JSON.stringify({
			version: 2,
			coupons: this._clippedCoupons
		}));
	}

	private _loadClippedCoupons() {
		const data = JSON.parse(this.storage.getItem("clipped_coupons") || "{}");
		if (!data.coupons) {
			return [];
		}
		return data.coupons;
	}

	private _handleAuthEvent = (e: CustomEvent) => {
		if (e.detail && e.detail.action === "logout") {
			this.clearClippedCoupons();
		}
	}
}
